import React, { useEffect, useState } from "react";
import { auth } from "../firebaseConfig";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { Table, Card, Button, Drawer, Typography, Space, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import AdminJobsMailer from "./AdminJobsMailer";
import BulkIssueUploader from "../components/BulkIssueUploader";
import FixDataComponent from "../components/FixDataComponent";
import DeleteEmptyTitleIssues from "../components/DeleteEmptyTitleIssues";

const { Title } = Typography;

export default function Admin() {
  const [automations, setAutomations] = useState([]);
  const [stats, setStats] = useState({ tools: [], issues: [] });
  const [user, setUser] = useState(null);
  const [viewType, setViewType] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    fetchAutomations();
    fetchStats();
    return () => unsub();
  }, []);

  const fetchAutomations = async () => {
    const snapshot = await getDocs(collection(db, "pending_automations"));
    const pending = snapshot.docs
      .filter(doc => doc.data().published === false)
      .map(doc => ({ id: doc.id, ...doc.data() }));
    setAutomations(pending);
  };

  const fetchStats = async () => {
    const [toolsSnap, issuesSnap] = await Promise.all([
      getDocs(collection(db, "pending_automations")),
      getDocs(collection(db, "common_issues")),
    ]);

    const tools = toolsSnap.docs
      .filter(doc => doc.data().published === true)
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());

    const issues = issuesSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());

    setStats({ tools, issues });
  };

  const approveAutomation = async (id) => {
    await updateDoc(doc(db, "pending_automations", id), { published: true });
    message.success("Automation approved!");
    fetchAutomations();
    fetchStats();
  };

  const rejectAutomation = async (id) => {
    await updateDoc(doc(db, "pending_automations", id), { published: "rejected" });
    message.info("Automation rejected.");
    fetchAutomations();
  };

  const showDrawer = (type) => {
    setViewType(type);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setViewType(null);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Admin Dashboard</Title>

      {/* Stats Section */}
      <Space direction="horizontal" size="large" style={{ marginBottom: 32 }}>
        <Card
          hoverable
          style={{ width: 300, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}
          onClick={() => showDrawer("tools")}
        >
          <Title level={4}>Tools</Title>
          <p>{stats.tools.length} Published Tools</p>
        </Card>
        <Card
          hoverable
          style={{ width: 300, backgroundColor: "#fff1f0", borderColor: "#ffa39e" }}
          onClick={() => showDrawer("issues")}
        >
          <Title level={4}>Issues</Title>
          <p>{stats.issues.length} Logged Issues</p>
        </Card>
      </Space>

      {/* Automation Approval */}
      <Title level={4}>Pending Automation Submissions</Title>
      {automations.length === 0 ? (
        <p>No pending submissions.</p>
      ) : (
        <Table
          dataSource={automations}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          columns={[
            { title: "Title", dataIndex: "title" },
            { title: "Description", dataIndex: "description" },
            { title: "File URL", dataIndex: "fileUrl" },
            { title: "LinkedIn", dataIndex: "linkedinUrl" },
            { title: "Prerequisites", dataIndex: "prerequisites" },
            { title: "Usage Steps", dataIndex: "usageSteps" },
            {
              title: "Created At",
              dataIndex: "createdAt",
              render: (v) => v?.toDate().toLocaleString(),
            },
            {
              title: "Actions",
              render: (_, record) => (
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => approveAutomation(record.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => rejectAutomation(record.id)}
                  >
                    Reject
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      {/* Drawer for Tools/Issues */}
      <Drawer
        title={viewType === "tools" ? "All Published Tools" : "All Logged Issues"}
        width={720}
        onClose={closeDrawer}
        visible={drawerVisible}
      >
        <Table
          dataSource={viewType === "tools" ? stats.tools : stats.issues}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          columns={
            viewType === "tools"
              ? [
                  { title: "Title", dataIndex: "title" },
                  { title: "Description", dataIndex: "description" },
                  {
                    title: "Created At",
                    dataIndex: "createdAt",
                    render: (v) => v?.toDate().toLocaleString(),
                  },
                ]
              : [
                  { title: "Title", dataIndex: "title" },
                  { title: "Symptoms", dataIndex: "symptoms" },
                  { title: "Resolution", dataIndex: "resolution" },
                  {
                    title: "Created At",
                    dataIndex: "createdAt",
                    render: (v) => v?.toDate().toLocaleString(),
                  },
                ]
          }
        />
      </Drawer>
      <AdminJobsMailer/>
      <BulkIssueUploader />
      <FixDataComponent/>
      <DeleteEmptyTitleIssues/>
    </div>
  );
}
